/** @file A generic, reusable button component for use in sidebars. */
import * as floatingUI from '@floating-ui/dom';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import { expr } from '@codegrade/utils';
import { PromiseButton } from '@/components/Button/PromiseButton';
import { Icon } from '@/components/Icon';
import { Popover } from '@/components/Popover/Popover';
import * as utils from '@/utils';
/** Middleware for positioning the sidebar button's tooltip. */
const sidebarTooltipMiddleware = [floatingUI.offset({ mainAxis: 4 })];
/** Base CSS classes for all sidebar items, interactive or not. */
const baseSidebarItemClasses = [
    'block',
    'w-12 min-h-12',
    'grid place-content-center',
];
/** CSS classes for interactive sidebar buttons. */
const sidebarButtonClasses = [
    baseSidebarItemClasses,
    'sidebar-btn', // Used by parent CSS to define hover/active states.
    'bg-white/0', // `bg-transparent` from Bootstrap can have `!important`.
    'text-inherit',
    'border-0',
    'transition duration-100',
];
/**
 * A versatile button component for sidebars, handling links, actions, and async actions.
 * It displays an icon and shows a descriptive tooltip on hover or focus.
 */
export const SidebarButton = tsx.componentFactory.create({
    name: 'app-sidebar-button',
    props: {
        /** The icon to display inside the button. If not provided, the default slot is used. */
        icon: p.ofType().optional,
        /** The main text label for the tooltip. If empty, no tooltip is shown. */
        label: p(String).optional,
        /** Optional secondary text for the tooltip. */
        sublabel: p(String).optional,
        /** The type of action the button performs. */
        type: p.ofType().required,
        /** Additional HTML attributes to pass to the button or link element. */
        buttonAttrs: p.ofType().optional,
    },
    computed: {
        /** @returns The content of the button, typically an icon and screen-reader text. */
        buttonContentNode() {
            if (this.icon === undefined) {
                return this.$slots.default;
            }
            return [
                <Icon class="size-5" icon={this.icon}/>,
                <span class="sr-only">
                    {[this.label, this.sublabel].filter(Boolean).join(' - ')}
                </span>,
            ];
        },
    },
    methods: {
        /**
         * Renders the core button/link/div element based on the `type` prop.
         *
         * @param labelTooltip Optional popover data to attach hover/focus listeners for the tooltip.
         * @returns The VNode for the button element.
         */
        renderButtonNode(labelTooltip) {
            let onProps = {};
            if (labelTooltip !== undefined) {
                onProps = {
                    mouseenter: labelTooltip.show,
                    mouseleave: labelTooltip.hide,
                    focus: labelTooltip.show,
                    blur: labelTooltip.hide,
                };
            }
            const testId = this.label?.toLowerCase().replaceAll(' ', '-') ?? 'untitled';
            const testIdBtn = `btn-${testId}`;
            const vnodedata = {
                attrs: {
                    'data-cy': testIdBtn,
                    ...this.buttonAttrs,
                },
            };
            return expr.match(this.type, 'tag', {
                'non-interactive': () => (<div class={baseSidebarItemClasses} {...vnodedata}>
                        {this.buttonContentNode}
                    </div>),
                'external-link': ({ href }) => (<a class={sidebarButtonClasses} target="_blank" href={href} on={onProps} {...vnodedata}>
                        {this.buttonContentNode}
                    </a>),
                link: ({ location, isActive }) => (<router-link class={[sidebarButtonClasses, { 'is-active': isActive }]} to={location} nativeOn={onProps} {...vnodedata}>
                        {this.buttonContentNode}
                    </router-link>),
                action: ({ action }) => (<button type="button" class={sidebarButtonClasses} onClick={action} on={onProps} {...vnodedata}>
                        {this.buttonContentNode}
                    </button>),
                'async-action': ({ action, loadingMessage, successMessage }) => (<PromiseButton class="block!" semantic="neutral" prominence={5} buttonClasses={['rounded-none', sidebarButtonClasses]} action={action} loadingAlertMessage={loadingMessage} successAlertMessage={successMessage} nativeOn={onProps} data-cy={testIdBtn}>
                        {this.buttonContentNode}
                    </PromiseButton>),
            });
        },
    },
    /** @returns The renderd the component, wrapping the button in a tooltip popover if a label is provided. */
    render() {
        if (this.label === undefined || this.label === '') {
            return this.renderButtonNode();
        }
        return (<Popover placement="right" class="contents" popoverAttrs={{
                class: ['p-2', 'bg-neutral-60 dark:bg-neutral-90 text-white', 'max-w-50'],
            }} middleware={sidebarTooltipMiddleware} scopedSlots={{
                button: data => this.renderButtonNode(data),
                popover: () => (<div class="grid gap-px">
                            <span class="label-medium">{this.label}</span>
                            {utils.ifJustOrEmpty(Maybe.fromNullable(this.sublabel).filter(str => str !== ''), sublabel => (<span class="label-small">{sublabel}</span>))}
                        </div>),
            }}/>);
    },
});
